import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clarityEvent } from '../utils/clarity';
import api from '../api/axios';
import ProductCard from '../components/ProductCard';
import BrandGrid from '../components/BrandGrid';
import ReactSlider from 'react-slider';
import { debounce } from 'lodash';
import './ProductsPage.css';
import RejectedSubmissions from '../components/RejectedSubmissions';

const ProductsPage = ({ userRole }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const effectiveRole = (() => {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        return params.get('preview') === 'employee' ? 'EMPLOYEE' : (userRole || user?.role);
    })();

    const [products, setProducts]       = useState([]);
    const [isLoading, setIsLoading]     = useState(false);
    const [error, setError]             = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore]         = useState(true);
    const [categories, setCategories]   = useState([]);
    const [categoryBrands, setCategoryBrands] = useState([]);
    const [viewMode, setViewMode]       = useState('grid');
    const [filterOpen, setFilterOpen]   = useState(false);
    const [pillCanScrollL, setPillCanScrollL] = useState(false);
    const [pillCanScrollR, setPillCanScrollR] = useState(false);
    const [brandPillCanScrollL, setBrandPillCanScrollL] = useState(false);
    const [brandPillCanScrollR, setBrandPillCanScrollR] = useState(false);
    const observer   = useRef();
    const filterRef  = useRef();
    const pillStripRef = useRef();
    const brandPillStripRef = useRef();

    const location     = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const updateFilterUrl = useCallback((updates = {}) => {
    const params = new URLSearchParams(location.search);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                params.delete(key);
            } else {
                params.set(key, String(value));
            }
        });

        navigate(`/products?${params.toString()}`, { replace: true });
    }, [location.search, navigate]);


    const initialCategory    = searchParams.get('category');
    const initialSubcategory = searchParams.get('subcategory');
    const initialBrand       = searchParams.get('brand');
    const initialSearch      = searchParams.get('search') || searchParams.get('q') || '';

    const [searchTerm,        setSearchTerm]        = useState(initialSearch);
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(initialSearch);
    const [sortOrder,         setSortOrder]         = useState('name,asc');
    // const [priceBounds,       setPriceBounds]       = useState({ min: 0, max: 10000 });
    // const [priceRange,        setPriceRange]        = useState([0, 10000]);
    // const [appliedPriceRange, setAppliedPriceRange] = useState(null);
    const urlMinPrice = searchParams.get('minPrice');
    const urlMaxPrice = searchParams.get('maxPrice');

    const [priceBounds, setPriceBounds] = useState({ min: 0, max: 10000 });

    const [priceRange, setPriceRange] = useState(
        urlMinPrice !== null && urlMaxPrice !== null
            ? [Number(urlMinPrice), Number(urlMaxPrice)]
            : [0, 10000]
    );

    const [appliedPriceRange, setAppliedPriceRange] = useState(
        urlMinPrice !== null && urlMaxPrice !== null
            ? [Number(urlMinPrice), Number(urlMaxPrice)]
            : null
    );
    const [activeCategory,    setActiveCategory]    = useState(initialCategory);

    // Close drawer on outside click
    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
        };
        if (filterOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [filterOpen]);

    // Categories
    useEffect(() => {
        api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {});
    }, []);

    // Brands within the active category
    useEffect(() => {
        const cat = activeCategory || initialCategory;
        if (!cat) { setCategoryBrands([]); return; }
        api.get('/products/brands-by-category', { params: { category: cat } })
            .then(r => setCategoryBrands(r.data || []))
            .catch(() => setCategoryBrands([]));
    }, [activeCategory, initialCategory]);

    // Pill strip scroll indicators
    const updatePillScroll = useCallback(() => {
        const el = pillStripRef.current;
        if (!el) return;
        setPillCanScrollL(el.scrollLeft > 4);
        setPillCanScrollR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    }, []);

    useEffect(() => {
        const el = pillStripRef.current;
        if (!el) return;
        const raf = requestAnimationFrame(() => updatePillScroll());
        el.addEventListener('scroll', updatePillScroll, { passive: true });
        const ro = new ResizeObserver(updatePillScroll);
        ro.observe(el);
        return () => { cancelAnimationFrame(raf); el.removeEventListener('scroll', updatePillScroll); ro.disconnect(); };
    }, [categories, updatePillScroll]);

    const scrollPills = (dir) => {
        const el = pillStripRef.current;
        if (el) el.scrollBy({ left: dir * 220, behavior: 'smooth' });
    };

    const updateBrandPillScroll = useCallback(() => {
        const el = brandPillStripRef.current;
        if (!el) return;
        setBrandPillCanScrollL(el.scrollLeft > 4);
        setBrandPillCanScrollR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    }, []);

    useEffect(() => {
        const el = brandPillStripRef.current;
        if (!el) return;
        const raf = requestAnimationFrame(() => updateBrandPillScroll());
        el.addEventListener('scroll', updateBrandPillScroll, { passive: true });
        const ro = new ResizeObserver(updateBrandPillScroll);
        ro.observe(el);
        return () => { cancelAnimationFrame(raf); el.removeEventListener('scroll', updateBrandPillScroll); ro.disconnect(); };
    }, [categoryBrands, updateBrandPillScroll]);

    const scrollBrandPills = (dir) => {
        const el = brandPillStripRef.current;
        if (el) el.scrollBy({ left: dir * 220, behavior: 'smooth' });
    };

    // Price bounds
    useEffect(() => {
        const fetchPriceBounds = async () => {
            try {
                const params = {
                    search: appliedSearchTerm,
                    category: activeCategory || initialCategory,
                    subcategory: initialSubcategory,
                    brand: initialBrand,
                };
                const res = await api.get('/products/price-range', { params });
                const min = Math.floor(res.data.minPrice) || 0;
                const max = Math.ceil(res.data.maxPrice)  || 10000;
                setPriceBounds(prev =>
                    prev.min === min && prev.max === max
                        ? prev
                        : { min, max }
                );
                // Restore price from URL if it exists.
                const urlMin = searchParams.get('minPrice');
                const urlMax = searchParams.get('maxPrice');
                if (urlMin !== null && urlMax !== null) {
                    const savedRange = [
                        Number(urlMin),
                        Number(urlMax)
                    ];
                    setPriceRange(savedRange);
                    setAppliedPriceRange(savedRange);
                } else {
                    // No saved price filter, use the full available range.
                    setPriceRange([min, max]);
                    setAppliedPriceRange([min, max]);
                }
            } catch {
                setPriceBounds({ min: 0, max: 10000 });
                const urlMin = searchParams.get('minPrice');
                const urlMax = searchParams.get('maxPrice');
                if (urlMin !== null && urlMax !== null) {
                    const savedRange = [
                        Number(urlMin),
                        Number(urlMax)
                    ];

                    setPriceRange(savedRange);
                    setAppliedPriceRange(savedRange);
                } else {
                    setPriceRange([0, 10000]);
                    setAppliedPriceRange([0, 10000]);
                }
            }
        };
        fetchPriceBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSearchTerm, activeCategory, initialCategory, initialSubcategory, initialBrand, searchParams]);

    // Products fetch
    useEffect(() => {
        if (!appliedPriceRange) return;
        const fetch = async () => {
            setIsLoading(true); setError(null);
            try {
                const params = {
                    page: currentPage, size: 24,
                    search: appliedSearchTerm, sort: sortOrder,
                    category: activeCategory || initialCategory,
                    subcategory: initialSubcategory,
                    brand: initialBrand,
                    minPrice: appliedPriceRange[0],
                    maxPrice: appliedPriceRange[1],
                };
                const res = await api.get('/products', { params });
                setProducts(prev => {
                    if (currentPage === 0) return res.data.content;
                    const next = res.data.content.filter(n => !prev.some(p => p.id === n.id));
                    return [...prev, ...next];
                });
                setHasMore(!res.data.last);
            } catch { setError('Failed to fetch products.'); }
            finally  { setIsLoading(false); }
        };
        fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortOrder, activeCategory, initialSubcategory, initialBrand, appliedPriceRange, appliedSearchTerm]);

    // Infinite scroll
    const lastProductRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) setCurrentPage(p => p + 1);
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    const debouncedApply = useMemo(() => debounce((search, price) => {
        setCurrentPage(0);
        setAppliedSearchTerm(search);
        setAppliedPriceRange(price);
        if (search) clarityEvent('search_performed');
    }, 500), []);

    const handleSearch = (e) => {const v = e.target.value; setSearchTerm(v); updateFilterUrl({ search: v || null }); debouncedApply(v, priceRange); };
    const handleSlider = (v) => { setPriceRange(v); updateFilterUrl({ minPrice: v[0], maxPrice: v[1]}); debouncedApply(searchTerm, v); clarityEvent('price_filter_used'); };
    const handleSort   = (e)  => { setSortOrder(e.target.value); setCurrentPage(0); clarityEvent('sort_order_changed'); };

    const handleCategoryClick = (catName) => {
        const next = activeCategory === catName ? null : catName;
        if (next) clarityEvent('category_selected');
        setActiveCategory(next);
        setCurrentPage(0); setProducts([]); setHasMore(true);
        const p = new URLSearchParams(location.search);
        if (next) p.set('category', next); else p.delete('category');
        navigate(`/products?${p.toString()}`, { replace: true });
    };

    const clearAllFilters = () => {
        setActiveCategory(null); setSearchTerm(''); setAppliedSearchTerm('');
        setCurrentPage(0); setProducts([]); setHasMore(true); setAppliedPriceRange(null);
        navigate('/products', { replace: true });
    };

    useEffect(() => {
        setCurrentPage(0); setProducts([]); setHasMore(true);
        setActiveCategory(initialCategory);
        const urlMin = searchParams.get('minPrice');
        const urlMax = searchParams.get('maxPrice');

        if (urlMin !== null && urlMax !== null) {
            const savedRange = [
                Number(urlMin),
                Number(urlMax)
            ];

            setPriceRange(savedRange);
            setAppliedPriceRange(savedRange);
        }
    }, [initialCategory, initialSubcategory, initialBrand, searchParams ]);

    useEffect(() => {
        setSearchTerm(initialSearch); setAppliedSearchTerm(initialSearch);
    }, [initialSearch]);

    const priceFiltered = appliedPriceRange &&
        (appliedPriceRange[0] > priceBounds.min || appliedPriceRange[1] < priceBounds.max);

    const hasActiveFilters = !!(activeCategory || searchTerm || initialBrand || priceFiltered);

    const showBrandGrid = !initialCategory && !activeCategory && !initialSubcategory && !initialBrand && !searchTerm;

    const heading = initialBrand                          ? `Brand: "${initialBrand}"`
        : (activeCategory || initialCategory)             ? (activeCategory || initialCategory)
        : initialSubcategory                              ? initialSubcategory
        : 'All Products';

    return (
        <div className="products-page-container">
            <RejectedSubmissions />
            {showBrandGrid && <BrandGrid />}

            <div className="product-list-section">

                {/* ── TOP ROW: heading left, controls right ── */}
                <div className="pp-toprow">
                    <h2 className="product-list-heading">{heading}</h2>

                    <div className="pp-controls">

                        {/* Search */}
                        <div className="pp-search-wrap">
                            <svg className="pp-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Search products…"
                                className="pp-search-input"
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>

                        {/* Sort */}
                        <select onChange={handleSort} value={sortOrder} className="pp-sort-select">
                            <option value="name,asc">Name A–Z</option>
                            <option value="name,desc">Name Z–A</option>
                            <option value="mrp,asc">Price ↑</option>
                            <option value="mrp,desc">Price ↓</option>
                        </select>

                        {/* Filter button + drawer */}
                        <div className="pp-filter-anchor" ref={filterRef}>
                            <button
                                className={`pp-filter-btn ${filterOpen ? 'is-open' : ''} ${hasActiveFilters ? 'has-active' : ''}`}
                                onClick={() => setFilterOpen(o => !o)}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
                                    <line x1="4" y1="6" x2="20" y2="6"/>
                                    <line x1="8" y1="12" x2="16" y2="12"/>
                                    <line x1="11" y1="18" x2="13" y2="18"/>
                                </svg>
                                <span>Filters</span>
                                {hasActiveFilters && <span className="pp-filter-pip" />}
                            </button>

                            <div className={`pp-drawer ${filterOpen ? 'pp-drawer--open' : ''}`}>
                                <div className="pp-drawer-header">
                                    <span className="pp-drawer-title">Filters</span>
                                    {hasActiveFilters && (
                                        <button className="pp-drawer-clear" onClick={() => { clearAllFilters(); setFilterOpen(false); }}>
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                <div className="pp-drawer-body">
                                    <div className="pp-drawer-section-label">Price Range</div>
                                    <div className="pp-drawer-prices">
                                        <span>₹{priceRange[0].toLocaleString('en-IN')}</span>
                                        <span>₹{priceRange[1].toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="pp-drawer-slider">
                                        <ReactSlider
                                            className="horizontal-slider"
                                            thumbClassName="slider-thumb"
                                            trackClassName="slider-track"
                                            value={priceRange}
                                            min={priceBounds.min}
                                            max={priceBounds.max}
                                            pearling
                                            minDistance={100}
                                            onChange={handleSlider}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* View toggle */}
                        <div className="pp-view-toggle">
                            <button
                                className={`pp-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                onClick={() => setViewMode('grid')}
                                title="Grid"
                            >
                                <svg viewBox="0 0 16 16" fill="currentColor" width="13" height="13">
                                    <rect x="0" y="0" width="7" height="7" rx="1.2"/>
                                    <rect x="9" y="0" width="7" height="7" rx="1.2"/>
                                    <rect x="0" y="9" width="7" height="7" rx="1.2"/>
                                    <rect x="9" y="9" width="7" height="7" rx="1.2"/>
                                </svg>
                            </button>
                            <button
                                className={`pp-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                                onClick={() => setViewMode('list')}
                                title="List"
                            >
                                <svg viewBox="0 0 16 16" fill="currentColor" width="13" height="13">
                                    <rect x="0" y="0"    width="16" height="3.5" rx="1"/>
                                    <rect x="0" y="6.25" width="16" height="3.5" rx="1"/>
                                    <rect x="0" y="12.5" width="16" height="3.5" rx="1"/>
                                </svg>
                            </button>
                        </div>

                    </div>{/* /pp-controls */}
                </div>{/* /pp-toprow */}

                {/* ── BRAND PILL STRIP — shown when a category is active ── */}
                {(initialCategory || activeCategory) && categoryBrands.length > 0 && (
                    <div className="pp-brand-pill-strip-outer">
                        <span className="pp-brand-pill-label">Brands</span>
                        <button
                            className={`pp-brand-pill-arrow pp-brand-pill-arrow--l${brandPillCanScrollL ? '' : ' pp-brand-pill-arrow--hidden'}`}
                            onClick={() => scrollBrandPills(-1)}
                            aria-label="Scroll brands left"
                            tabIndex={brandPillCanScrollL ? 0 : -1}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <div className="pp-brand-pill-strip" ref={brandPillStripRef}>
                            <button
                                className={`pp-brand-pill${!initialBrand ? ' pp-brand-pill--active' : ''}`}
                                onClick={() => {
                                    const cat = activeCategory || initialCategory;
                                    const params = new URLSearchParams(location.search);
                                    if (cat) params.set('category', cat);
                                    navigate(`/products?${params.toString()}`);
                                }}
                            >
                                All Brands
                            </button>
                            {categoryBrands.map(brand => (
                                <button
                                    key={brand}
                                    className={`pp-brand-pill${initialBrand === brand ? ' pp-brand-pill--active' : ''}`}
                                    onClick={() => {
                                        const cat = activeCategory || initialCategory;
                                        const params = new URLSearchParams(location.search);
                                        if (cat) params.set('category', cat);
                                        params.set('brand', brand);
                                        navigate(`/products?${params.toString()}`);
                                    }}
                                >
                                    {brand}
                                </button>
                            ))}
                        </div>
                        {brandPillCanScrollL && <div className="pp-brand-pill-fade pp-brand-pill-fade--l" />}
                        {brandPillCanScrollR && <div className="pp-brand-pill-fade pp-brand-pill-fade--r" />}
                        <button
                            className={`pp-brand-pill-arrow pp-brand-pill-arrow--r${brandPillCanScrollR ? '' : ' pp-brand-pill-arrow--hidden'}`}
                            onClick={() => scrollBrandPills(1)}
                            aria-label="Scroll brands right"
                            tabIndex={brandPillCanScrollR ? 0 : -1}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                    </div>
                )}

                {/* ── CATEGORY PILL STRIP — hidden only when browsing a standalone brand (no category) ── */}
                {categories.length > 0 && (!initialBrand || initialCategory || activeCategory) && (
                    <div className="pp-pill-strip-outer">
                        <button
                            className={`pp-pill-arrow pp-pill-arrow--l${pillCanScrollL ? '' : ' pp-pill-arrow--hidden'}`}
                            onClick={() => scrollPills(-1)}
                            aria-label="Scroll categories left"
                            tabIndex={pillCanScrollL ? 0 : -1}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <div className="pp-pill-strip" ref={pillStripRef}>
                            <button
                                className={`pp-pill ${!activeCategory && !initialCategory ? 'pp-pill--active' : ''}`}
                                onClick={() => handleCategoryClick(null)}
                            >
                                All
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id || cat.name}
                                    className={`pp-pill ${activeCategory === cat.name ? 'pp-pill--active' : ''}`}
                                    onClick={() => handleCategoryClick(cat.name)}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                        {pillCanScrollL && <div className="pp-pill-fade pp-pill-fade--l" />}
                        {pillCanScrollR && <div className="pp-pill-fade pp-pill-fade--r" />}
                        <button
                            className={`pp-pill-arrow pp-pill-arrow--r${pillCanScrollR ? '' : ' pp-pill-arrow--hidden'}`}
                            onClick={() => scrollPills(1)}
                            aria-label="Scroll categories right"
                            tabIndex={pillCanScrollR ? 0 : -1}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                    </div>
                )}

                {/* ── ACTIVE FILTER CHIPS ── */}
                {hasActiveFilters && (
                    <div className="pp-chips">
                        {(activeCategory || initialCategory) && (
                            <span className="pp-chip">
                                {activeCategory || initialCategory}
                                <button onClick={() => handleCategoryClick(activeCategory || initialCategory)}>✕</button>
                            </span>
                        )}
                        {searchTerm && (
                            <span className="pp-chip">
                                "{searchTerm}"
                                <button onClick={() => { setSearchTerm(''); updateFilterUrl({ search: null }); debouncedApply('', priceRange); }}>✕</button>
                            </span>
                        )}
                        {initialBrand && <span className="pp-chip">{initialBrand}</span>}
                        {priceFiltered && (
                            <span className="pp-chip">
                                ₹{appliedPriceRange[0].toLocaleString('en-IN')} – ₹{appliedPriceRange[1].toLocaleString('en-IN')}
                                <button onClick={() => { const r = [priceBounds.min, priceBounds.max]; setPriceRange(r); updateFilterUrl({minPrice: null, maxPrice:null}); debouncedApply(searchTerm, r); }}>✕</button>
                            </span>
                        )}
                    </div>
                )}

                {/* ── PRODUCTS GRID ── */}
                {products.length === 0 && isLoading && currentPage === 0
                    ? (
                        <div className="products-grid-skeleton">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="pp-skeleton-card">
                                    <div className="pp-skeleton-img" />
                                    <div className="pp-skeleton-body">
                                        <div className="pp-skeleton-line pp-skeleton-line--short" />
                                        <div className="pp-skeleton-line pp-skeleton-line--medium" />
                                        <div className="pp-skeleton-line pp-skeleton-line--short" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <main className={`products-grid ${viewMode === 'list' ? 'products-grid--list' : ''}`}>
                            {products.map((product, index) => {
                                const isLast = products.length === index + 1;
                                const card   = <ProductCard key={product.id} product={product} userRole={effectiveRole} />;
                                return isLast
                                    ? <div ref={lastProductRef} key={product.id}>{card}</div>
                                    : card;
                            })}
                        </main>
                    )
                }

                {isLoading && products.length > 0 && <div className="loading-state">Loading more…</div>}

                {!hasMore && products.length > 0 && (
                    <p className="pp-end-msg">— {products.length} products shown —</p>
                )}

                {!isLoading && products.length === 0 && !error && (
                    <div className="no-products-found">No products found.</div>
                )}

                {error && <div className="error-state">{error}</div>}

            </div>
        </div>
    );
};

export default ProductsPage;